import { FiShield, FiZap, FiGlobe, FiCpu, FiPlusCircle } from "react-icons/fi";
import { DiDjango, DiPython } from "react-icons/di";
import { SiPostgresql } from "react-icons/si";
import { LuSettings, LuUsers, LuShieldCheck, LuPercent, LuShield, LuCircleCheck, LuCpu, LuClipboardList, LuCode, LuBug, LuRocket, LuDatabase, LuClock } from "react-icons/lu";

export const djangoBenefits = [
  {
    icon: <FiPlusCircle className="w-8 h-8" />,
    title: "Batteries Included",
    desc: "Django comes with everything you need out of the box, from admin interfaces to authentication and ORM.",
  },
  {
    icon: <FiShield className="w-8 h-8" />,
    title: "Security by Default",
    desc: "Provides strong protection against common web threats like SQL injection, XSS, and CSRF automatically.",
  },
  {
    icon: <FiZap className="w-8 h-8" />,
    title: "Rapid Development",
    desc: "Follows the 'Don't Repeat Yourself' (DRY) principle to help developers write clean code very quickly.",
  },
];

export const djangoServices = [
  {
    icon: <DiDjango size={36} />,
    title: "Enterprise Python Web",
    description: "Building robust, scalable, and secure backend systems for enterprise-grade applications.",
    image: "/images/api-dev.jpg",
    link: "#",
  },
  {
    icon: <DiPython size={36} />,
    title: "AI/ML Integration",
    description: "Seamlessly connecting your web backend with Python's powerful AI and data science ecosystem.",
    image: "/images/backend-dev.jpg",
    link: "#",
  },
  {
    icon: <SiPostgresql size={36} />,
    title: "Complex Data Systems",
    description: "Engineering data-intensive backends with Django's powerful ORM and complex query capabilities.",
    image: "/images/custom-dev.jpg",
    link: "#",
  },
];

export const djangoFaqs = [
  {
    q: "Why use Django for my project?",
    a: "Django is ideal for projects that need to be built quickly, securely, and need to scale to handle massive amounts of content.",
  },
  {
    q: "Is it good for AI/ML projects?",
    a: "Yes, being Python-based, Django is the natural choice for integrating machine learning models into web applications.",
  },
];

export const djangoProcessSteps = [
  {
    step: "01",
    title: "Model Design",
    description: "Designing the data models and relationships using Django's powerful ORM.",
    points: ["ORM mapping", "Relationship design", "Migration planning"],
  },
  {
    step: "02",
    title: "Logic & Views",
    description: "Developing the core business logic and RESTful endpoints using Django REST Framework.",
    points: ["DRF endpoint creation", "Authentication setup", "Logic implementation"],
  },
  {
    step: "03",
    title: "Deployment & Scale",
    description: "Optimizing database queries and deploying using industry-standard tools like Gunicorn and Nginx.",
    points: ["Query optimization", "Server configuration", "Load testing"],
  },
];

export const djangoTechStack = ["Django", "Python", "Django REST Framework", "PostgreSQL", "Redis", "Celery", "Docker"];

export const djangoHeroData = {
  backgroundImage: "/images/serviceBanners/djangoBanner.webp",
  whiteImage: "/images/serviceBanners/DjangoW.webp",
  subTitle: "Django Development Services",
  title: (
    <>
      Secure Django <br /> <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-purple-600">Python Backends</span>
    </>
  ),
  description:
    "We build secure, scalable, and high performance backend systems using Django framework with Python to deliver enterprise ready applications, strong security, and efficient architecture for modern digital businesses.",
  primaryButton: {
    title: "Get Started",
    onClick: () => console.log("Get Started clicked"),
  },
  secondaryButton: {
    title: "View Our Work",
    onClick: () => console.log("View Work clicked"),
  },
  featureData: [
    {
      icon: "/images/services/website/high_performance.webp",
      title: "Batteries Included",
      desc: "Built-in admin, authentication, and ORM system for fast development.",
      iconColor: "#a855f7",
    },
    {
      icon: "/images/services/website/responsive.webp",
      title: "Pythonic Logic",
      desc: "Clean Python-based architecture ideal for scalable applications.",
      iconColor: "#22d3ee",
    },
    {
      icon: "/images/services/website/secure.webp",
      title: "Built-in Security",
      desc: "Automatic protection against XSS, SQL injection, and CSRF attacks.",
      iconColor: "#a855f7",
    },
    {
      icon: "/images/services/website/clean_code.webp",
      title: "Django ORM",
      desc: "Efficient and type-safe database management using ORM system.",
      iconColor: "#34d399",
    },
  ],
  floatingIcons: [
    {
      image: "/images/services/website/nodejs.webp", // Re-use generic icon or replace with django path asset
      top: "16%",
      right: "8%",
      containerSize: 80,
      iconSize: 60,
      glowColor: "rgba(59,130,246,0.35)",
      iconGlow: "rgba(59,130,246,0.9)",
    },
  ],
};

export const djangoStatsData = [
  {
    icon: LuSettings,
    value: "150+",
    label: "Django Projects",
    colorClass: "text-[#00d2ff]",
    bgClass: "bg-[#00d2ff]/10",
    borderClass: "border-[#00d2ff]/20",
    glowClass: "shadow-[0_0_20px_rgba(0,210,255,0.15)]",
  },
  {
    icon: LuUsers,
    value: "65+",
    label: "Happy Clients",
    colorClass: "text-[#a855f7]",
    bgClass: "bg-[#a855f7]/10",
    borderClass: "border-[#a855f7]/20",
    glowClass: "shadow-[0_0_20px_rgba(168,85,247,0.15)]",
  },
  {
    icon: LuShieldCheck,
    value: "99.99%",
    label: "Service Uptime",
    colorClass: "text-[#06b6d4]",
    bgClass: "bg-[#06b6d4]/10",
    borderClass: "border-[#06b6d4]/20",
    glowClass: "shadow-[0_0_20px_rgba(6,182,212,0.15)]",
  },
  {
    icon: LuCpu,
    value: "10M+",
    label: "Daily Requests",
    colorClass: "text-[#3b82f6]",
    bgClass: "bg-[#3b82f6]/10",
    borderClass: "border-[#3b82f6]/20",
    glowClass: "shadow-[0_0_20px_rgba(59,130,246,0.15)]",
  },
  {
    icon: LuPercent,
    value: "40%",
    label: "Faster Development",
    colorClass: "text-[#d946ef]",
    bgClass: "bg-[#d946ef]/10",
    borderClass: "border-[#d946ef]/20",
    glowClass: "shadow-[0_0_20px_rgba(217,70,239,0.15)]",
  },
  {
    icon: LuShield,
    value: "100%",
    label: "Threat Proof",
    colorClass: "text-[#6366f1]",
    bgClass: "bg-[#6366f1]/10",
    borderClass: "border-[#6366f1]/20",
    glowClass: "shadow-[0_0_20px_rgba(99,102,241,0.15)]",
  },
];

export const djangoSolutionsAndProcessData = {
  buildSubtitle: "WHAT WE BUILD",
  buildTitle: "Django Python Solutions We Build",
  buildCards: [
    {
      title: "Secure Web Applications",
      desc: "Build clean Django-based web apps using templates or REST APIs.",
      icon: LuSettings,
      colorClass: "text-[#00d2ff]",
      bgClass: "bg-[#00d2ff]/10",
      borderClass: "border-[#00d2ff]/20 hover:border-[#00d2ff]/50",
      glowClass: "group-hover:shadow-[0_0_20px_rgba(0,210,255,0.25)]",
    },
    {
      title: "Django REST APIs",
      desc: "Create scalable and secure JSON APIs using Django REST Framework.",
      icon: LuCode,
      colorClass: "text-[#3b82f6]",
      bgClass: "bg-[#3b82f6]/10",
      borderClass: "border-[#3b82f6]/20 hover:border-[#3b82f6]/50",
      glowClass: "group-hover:shadow-[0_0_20px_rgba(59,130,246,0.25)]",
    },
    {
      title: "AI/ML Integrations",
      desc: "Integrate machine learning models into Django backend workflows.",
      icon: LuCpu,
      colorClass: "text-[#a855f7]",
      bgClass: "bg-[#a855f7]/10",
      borderClass: "border-[#a855f7]/20 hover:border-[#a855f7]/50",
      glowClass: "group-hover:shadow-[0_0_20px_rgba(168,85,247,0.25)]",
    },
    {
      title: "Data-Intensive Backends",
      desc: "Handle complex queries and caching using PostgreSQL, Redis, and ORM.",
      icon: LuDatabase,
      colorClass: "text-[#06b6d4]",
      bgClass: "bg-[#06b6d4]/10",
      borderClass: "border-[#06b6d4]/20 hover:border-[#06b6d4]/50",
      glowClass: "group-hover:shadow-[0_0_20px_rgba(6,182,212,0.25)]",
    },
    {
      title: "Celery Task Processing",
      desc: "Run background tasks like emails and cron jobs using Celery workers.",
      icon: LuClock,
      colorClass: "text-[#6366f1]",
      bgClass: "bg-[#6366f1]/10",
      borderClass: "border-[#6366f1]/20 hover:border-[#6366f1]/50",
      glowClass: "group-hover:shadow-[0_0_20px_rgba(99,102,241,0.25)]",
    },
    {
      title: "Custom Admin Panels",
      desc: "Customize Django admin with advanced filters and business logic tools.",
      icon: LuRocket,
      colorClass: "text-[#d946ef]",
      bgClass: "bg-[#d946ef]/10",
      borderClass: "border-[#d946ef]/20 hover:border-[#d946ef]/50",
      glowClass: "group-hover:shadow-[0_0_20px_rgba(217,70,239,0.25)]",
    },
  ],
  processSubtitle: "DJANGO ENGINEERING PROCESS",
  processTitle: "Django Engineering Process",
  processSteps: [
    {
      step: "01",
      title: "Model Architecture",
      description: "Design Django ORM schemas with proper relationships and migrations.",
      icon: LuClipboardList,
    },
    {
      step: "02",
      title: "View & Serializer Setup",
      description: "Create REST APIs using Django views and serializers with routing.",
      icon: LuCode,
    },
    {
      step: "03",
      title: "Security Implementation",
      description: "Enable CSRF protection, CORS policies, and token-based authentication.",
      icon: LuShieldCheck,
    },
    {
      step: "04",
      title: "Async Task Integration",
      description: "Configure Celery workers with Redis for background processing.",
      icon: LuSettings,
    },
    {
      step: "05",
      title: "Database Profiling",
      description: "Optimize queries using Django debug tools and indexing strategies.",
      icon: LuCircleCheck, // Updated to a safe Lucide icon variant to ensure clean builds
    },
    {
      step: "06",
      title: "Docker Deployment",
      description: "Containerize applications and deploy using Gunicorn for production.",
      icon: LuRocket,
    },
  ],
  benefitsSubtitle: "WHY CHOOSE DJANGO?",
  benefitsTitle: "Why Django is the Right Choice",
  benefits: [
    "Batteries-included framework",
    "Built-in secure system",
    "Python ML compatibility",
    "Clean ORM architecture",
    "Rapid development cycle",
    "Celery Redis scaling",
    "Strong open-source support",
  ],
  logoImage: "/images/serviceBanners/djangoBanner.webp",
  whiteLogoImage: "/images/serviceBanners/DjangoW.webp",
};

export const djangoPortfolioData = {
  subtitle: "OUR WORK",
  title: "Django Backends We're Proud Of",
  buttonText: "View All Projects",
  buttonLink: "#all-projects",
  apps: [
    {
      title: "MedRecord Analytics",
      badge: "Django & Celery Portal",
      badgeColor: "bg-[#00d2ff]/10 text-[#00d2ff] border-[#00d2ff]/20",
      desc: "Highly secure backend generating patient analytics and processing raw medical files asynchronously.",
      downloads: "500k Patients",
      rating: "5.0",
      image: "/images/WebAppDevelopment/django_development.png",
    },
    {
      title: "AutoFeed ML Hub",
      badge: "DRF & Python ML",
      badgeColor: "bg-[#a855f7]/10 text-[#a855f7] border-[#a855f7]/20",
      desc: "API gateway serving predictive recommendation engines and sorting dynamic product lists.",
      downloads: "10M+ Daily API Hits",
      rating: "4.9",
      image: "/images/WebAppDevelopment/django_development.png",
    },
  ],
};

export const djangoTechnologiesData = {
  techTitle: "TECHNOLOGIES WE USE",
  technologies: [
    { name: "REST API", icon: "RestAPI" },
    { name: "SQLite", icon: "SQLite" },
    { name: "Firebase", icon: "Firebase" },
    { name: "Provider", icon: "Provider" },
  ],
  trustTitle: "TRUSTED BY BUSINESSES WORLDWIDE",
  trustClients: [
    { image: "/images/logo/google_logo.png", alt: "google_logo" },
    { image: "/images/logo/microsoft_logo.png", alt: "microsoft_logo" },
    { image: "/images/logo/amazon_logo.png", alt: "amazon_logo" },
    { image: "/images/logo/Adobe_Corporate_logo.png", alt: "adobe_logo" },
    { image: "/images/logo/IBM_logo.png", alt: "ibm_logo" },
    { image: "/images/logo/Oracle_logo.png", alt: "oracle_logo" },
  ],
};

export const djangoAboutData = [
  {
    title: "Django Web Development Company for Secure and Scalable Applications",
    content: (
      <>
        Modern businesses require backend systems that remain secure, flexible, and capable of managing growing operational demands. A trusted{" "}
        <b className="text-text-primary font-semibold">django web development company</b> helps organizations create scalable digital applications designed for performance and
        long-term reliability. Many businesses struggle with backend systems that become difficult to maintain as data and user demands increase. At{" "}
        <b className="text-text-primary font-semibold">Tycho Technologies</b>, Django solutions are developed around business goals, usability, and performance requirements to help
        organizations create dependable digital ecosystems that support sustainable growth.
      </>
    ),
  },
  {
    title: "Python Web Development Services for Smarter Digital Solutions",
    content: (
      <>
        Building successful applications requires technologies that support flexibility, speed, and strong backend performance. Reliable{" "}
        <b className="text-text-primary font-semibold">python web development services</b> help businesses create structured systems that improve workflow efficiency and support
        complex operational requirements. Companies investing in <b className="text-text-primary font-semibold">custom backend development</b> often benefit from better scalability
        because applications can adapt more effectively to evolving business needs. Tycho Technologies focuses on practical Python-based frameworks designed to improve operational
        stability while supporting long-term digital transformation.
      </>
    ),
  },
  {
    title: "Hire Python Developers for Better Application Performance",
    content: (
      <>
        Growing businesses need backend environments that remain responsive and adaptable as customer expectations change. Companies looking to{" "}
        <b className="text-text-primary font-semibold">hire python developers</b> often prioritize flexibility, performance, and maintainability to support future-ready application
        ecosystems. Organizations aiming to improve backend efficiency frequently strengthen digital infrastructures with{" "}
        <a href="https://tychotechnologies.com/codeigniter-development-services-india" className="text-cyan-400 underline hover:text-cyan-300">
          codeigniter development services
        </a>{" "}
        to support connected business systems and better operational performance.
      </>
    ),
  },
  {
    title: "Why Businesses Choose a Django Development Company in India",
    content: (
      <>
        The demand for a trusted <b className="text-text-primary font-semibold">django development company in india</b> continues to grow as organizations seek secure and scalable
        web application frameworks. Companies investing in <b className="text-text-primary font-semibold">python development services india</b> often focus on stronger backend
        reliability, operational flexibility, and scalable application performance. Businesses partnering with Tycho Technologies gain access to customized Django strategies
        designed around measurable business objectives and technical requirements. Many organizations also improve backend adaptability through{" "}
        <a href="https://tychotechnologies.com/laravel-development-services-india" className="text-cyan-400 underline hover:text-cyan-300">
          laravel development services
        </a>{" "}
        to create stronger and future-ready enterprise systems.
      </>
    ),
  },
];
